import { useMemo, useState } from 'react';
import { Platform, ScrollView, View, useColorScheme, useWindowDimensions } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Appbar, Icon, SegmentedButtons, Surface, Text, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import Logo from '../../../../components/Logo';
import { formatClock } from '../../../../lib/drivingLimit';
import { DriverLimitsCard } from '../DriverLimitsCard';
import { WorkStateActions } from '../WorkStateActions';
import { WorkTimeStatusPanel } from '../WorkTimeStatusPanel';
import {
  LIMITS_SCENARIOS,
  WORK_STATES,
  mockLimits,
  mockSnapshot,
  type LimitsScenario,
} from '../mocks';
import type { DriverWorkState } from '../types';
import { NotificationMock } from './NotificationMock';

// Design preview for ALL-136 (driver state header + driving limits card) with
// example data. It mirrors the layout of DashboardContent without the map,
// the sidebar or any API call. Not linked from the app.

export type PreviewView = 'home' | 'limits' | 'notifications';

const SIDEBAR_WIDTH = 320;

const MapPlaceholder = () => {
  const theme = useTheme();
  const scheme = useColorScheme();
  const line = scheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';
  return (
    <View
      pointerEvents="none"
      style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: theme.colors.surfaceVariant }}
    >
      {Array.from({ length: 14 }).map((_, i) => (
        <View
          key={`h${i}`}
          style={{ position: 'absolute', left: 0, right: 0, top: `${i * 8}%`, height: i % 4 === 0 ? 10 : 3, backgroundColor: line }}
        />
      ))}
      {Array.from({ length: 10 }).map((_, i) => (
        <View
          key={`v${i}`}
          style={{ position: 'absolute', top: 0, bottom: 0, left: `${i * 11}%`, width: i % 3 === 0 ? 10 : 3, backgroundColor: line }}
        />
      ))}
    </View>
  );
};

const SidebarPlaceholder = () => {
  const theme = useTheme();
  return (
    <View
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: SIDEBAR_WIDTH,
        backgroundColor: theme.colors.surface,
        borderRightWidth: 1,
        borderRightColor: theme.colors.outlineVariant,
        padding: 24,
        gap: 18,
      }}
    >
      <Logo size="medium" />
      {[70, 55, 62, 48, 58].map((w, i) => (
        <View key={i} style={{ height: 12, width: `${w}%`, borderRadius: 999, backgroundColor: theme.colors.outlineVariant, opacity: 0.6 }} />
      ))}
    </View>
  );
};

const noop = () => undefined;

export const WorkTimeDesignPreview = ({
  initialState = 'driving',
  initialScenario = 'ok',
  initialView = 'home',
  captureMode = false,
}: {
  initialState?: DriverWorkState;
  initialScenario?: LimitsScenario;
  initialView?: PreviewView;
  captureMode?: boolean;
}) => {
  const theme = useTheme();
  const scheme = useColorScheme();
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const isLargeScreen = width > 1024;
  const isTablet = width >= 768 && width <= 1024;
  const stacked = !isLargeScreen && !isTablet;

  const [state, setState] = useState<DriverWorkState>(initialState);
  const [scenario, setScenario] = useState<LimitsScenario>(initialScenario);
  const [view, setView] = useState<PreviewView>(initialView);
  // Fixed per state/scenario so the timers and the «volta às» time are stable.
  const nowMs = useMemo(() => Date.now(), [state, scenario]);

  const limits = useMemo(() => mockLimits(scenario, state === 'driving', nowMs), [scenario, state, nowMs]);
  const snapshot = useMemo(() => mockSnapshot(state, nowMs), [state, nowMs]);

  const go = (next: DriverWorkState) => setState(next);
  const handlers = {
    onStartSession: () => go('available'),
    onEndSession: () => go('resting'),
    onStartDriving: () => go('driving'),
    onStopDriving: () => go('available'),
  };

  const contentLeft = isLargeScreen ? SIDEBAR_WIDTH : 0;
  const available = width - contentLeft;
  const contentMaxWidth = isLargeScreen
    ? Math.min(800, available * 0.7)
    : isTablet
      ? Math.min(600, width * 0.8)
      : Math.min(400, width * 0.9);

  const controls = captureMode ? null : (
    <Surface elevation={2} style={{ padding: 8, gap: 6, backgroundColor: theme.colors.surface }} testID="preview-controls">
      <Text variant="labelMedium" style={{ color: theme.colors.onSurfaceVariant }}>
        ALL-136 · pré-visualização com dados de exemplo
      </Text>
      <SegmentedButtons
        density="small"
        value={view}
        onValueChange={(v) => setView(v as PreviewView)}
        buttons={[
          { value: 'home', label: 'Home' },
          { value: 'limits', label: 'Limites' },
          { value: 'notifications', label: 'Notificações' },
        ]}
      />
      <SegmentedButtons
        density="small"
        value={state}
        onValueChange={(v) => go(v as DriverWorkState)}
        buttons={WORK_STATES.map((s) => ({ value: s, label: t(`workTime.states.${s}`) }))}
      />
      <SegmentedButtons
        density="small"
        value={scenario}
        onValueChange={(v) => setScenario(v as LimitsScenario)}
        buttons={LIMITS_SCENARIOS.map((s) => ({ value: s, label: s }))}
      />
    </Surface>
  );

  if (view === 'notifications') {
    return (
      <View style={{ flex: 1, backgroundColor: theme.colors.background }}>
        {controls}
        <NotificationsSheet nowMs={nowMs} wide={!stacked} />
      </View>
    );
  }

  const headerHeight = isLargeScreen ? 0 : 48;

  return (
    <View style={{ flex: 1, backgroundColor: theme.colors.background }} testID="all136-preview">
      {controls}
      <View style={{ flex: 1 }}>
        {isLargeScreen ? <SidebarPlaceholder /> : null}
        {!isLargeScreen ? (
          <Appbar.Header style={{ height: headerHeight, backgroundColor: theme.colors.surface }} statusBarHeight={0}>
            <Appbar.Action icon="menu" onPress={noop} color={theme.colors.onSurface} />
            <View style={{ flex: 1, alignItems: 'center' }}>
              <Logo size={Platform.OS === 'web' ? 'medium' : 'mobile'} />
            </View>
            <View style={{ width: 48 }} />
          </Appbar.Header>
        ) : null}

        <View style={{ flex: 1, marginLeft: contentLeft }}>
          <MapPlaceholder />

          {view === 'limits' ? (
            <View
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: theme.colors.backdrop,
                zIndex: 3,
              }}
            >
              <ScrollView contentContainerStyle={{ alignItems: 'center', padding: 16 }}>
                <View style={{ width: Math.min(560, available - 32), maxWidth: '100%' }}>
                  <DriverLimitsCard limits={limits} nowMs={nowMs} onClose={() => setView('home')} />
                </View>
              </ScrollView>
            </View>
          ) : null}

          <View style={{ position: 'absolute', top: 16, left: 0, right: 0, alignItems: 'center', zIndex: 1 }}>
            <Surface
              elevation={4}
              style={{
                padding: isTablet ? 20 : 16,
                borderRadius: 16,
                backgroundColor: theme.colors.surface,
                width: contentMaxWidth,
                maxWidth: isLargeScreen ? 800 : isTablet ? 600 : 400,
                borderWidth: 1,
                borderColor: theme.colors.outline,
              }}
            >
              <WorkTimeStatusPanel
                snapshot={snapshot}
                limits={limits}
                onOpenLimits={() => setView('limits')}
                large={isTablet}
                nowMs={nowMs}
              />
            </Surface>
          </View>

          <View
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: scheme === 'dark' ? 'rgba(32, 32, 32, 0.8)' : 'rgba(255, 255, 255, 0.8)',
              borderTopWidth: 1,
              borderTopColor: theme.colors.outline,
              zIndex: 2,
            }}
          >
            <SafeAreaView edges={['bottom']}>
              <View style={{ padding: 16, width: stacked ? '100%' : contentMaxWidth, alignSelf: 'center' }}>
                <WorkStateActions state={state} stacked={stacked} handlers={handlers} />
              </View>
            </SafeAreaView>
          </View>
        </View>
      </View>
    </View>
  );
};

const NotificationsSheet = ({ nowMs, wide }: { nowMs: number; wide: boolean }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const n = (k: string, o?: Record<string, unknown>) => t(`workTime.notifications.${k}`, o);
  const time = (agoMin: number) => formatClock(new Date(nowMs - agoMin * 60000).toISOString()) ?? '';
  const proposed = 'Proposta';
  const changed = 'Muda';
  const items = [
    {
      caption: 'Fixa · A conduzir (texto novo, mesmas ações)',
      status: 'changed' as const,
      statusLabel: changed,
      title: t('workTime.states.driving'),
      body: n('ongoingBody', { time: time(47) }),
      actions: [t('notifications.stopDrivingButton')],
      sticky: true,
    },
    {
      caption: 'Fixa · Sessão ativa (texto novo, mesmas ações)',
      status: 'changed' as const,
      statusLabel: changed,
      title: t('workTime.states.available'),
      body: n('ongoingBody', { time: time(12) }),
      actions: [t('notifications.startDrivingButton')],
      sticky: true,
    },
    {
      caption: 'Aviso das 10 h (igual ao atual, ALL-41)',
      status: 'unchanged' as const,
      statusLabel: 'Sem alteração',
      title: t('drivingLimit.warningTitle'),
      body: t('drivingLimit.warningBody', { minutes: 60, hours: '10' }),
      actions: [],
    },
    {
      caption: 'Condução na semana aos 80 % e aos 95 %',
      status: 'new' as const,
      statusLabel: proposed,
      title: n('weekWarningTitle', { used: 48, limit: 60 }),
      body: n('weekWarningBody', { remaining: 12 }),
      actions: [],
    },
    {
      caption: 'Média de condução em 4 meses aos 80 % e aos 95 %',
      status: 'new' as const,
      statusLabel: proposed,
      title: n('fourMonthWarningTitle'),
      body: n('fourMonthWarningBody', { avg: '46,1', limit: 48 }),
      actions: [],
    },
  ];
  return (
    <ScrollView contentContainerStyle={{ padding: 16, alignItems: 'center' }} testID="all136-notifications">
      <View style={{ width: '100%', maxWidth: wide ? 1040 : 420, gap: 12 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Icon source="bell-outline" size={22} color={theme.colors.primary} />
          <Text variant="titleLarge" style={{ color: theme.colors.onSurface }}>
            Notificações Android · proposta de texto e ações
          </Text>
        </View>
        <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
          Desenho estático com dados de exemplo. Os alertas de movimento e de paragem ficam como hoje. As capturas no dispositivo chegam quando o Dev ligar as notificações.
        </Text>
        <View style={{ flexDirection: wide ? 'row' : 'column', flexWrap: 'wrap', gap: 16 }}>
          {items.map((it) => (
            <View key={it.caption} style={{ width: wide ? 500 : '100%' }}>
              <NotificationMock {...it} />
            </View>
          ))}
        </View>
      </View>
    </ScrollView>
  );
};
